import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, ChevronDown, Zap, Shield, Settings, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import ElectricCursor from '../components/ElectricCursor';
import { Reveal } from '../hooks/useReveal';

const HERO_BG = 'https://images.unsplash.com/photo-1771012447460-116dec149e1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxibHVlJTIwZWxlY3RyaWNhbCUyMGFic3RyYWN0fGVufDB8fHx8MTc3NTkzODM0Mnww&ixlib=rb-4.1.0&q=85';
const ABOUT_IMG = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/n2sltbd7_WhatsApp%20Image%202024-11-17%20at%2021.04.45%20%281%29.jpeg';
const TRANSFORMER_1 = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/3wifgtrh_WhatsApp%20Image%202024-11-17%20at%2021.04.50.jpeg';
const TRANSFORMER_2 = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/n2sltbd7_WhatsApp%20Image%202024-11-17%20at%2021.04.45%20%281%29.jpeg';
const TRANSFORMER_3 = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/gfe4xo80_WhatsApp%20Image%202024-11-17%20at%2021.04.49%20%281%29.jpeg';

export default function Home() {
  const { t } = useLanguage();

  const products = [
    { key: 'distribution', img: TRANSFORMER_1, kva: '50 - 2,500 kVA' },
    { key: 'power', img: TRANSFORMER_2, kva: '2,500 - 16,000 kVA' },
    { key: 'custom', img: TRANSFORMER_3, kva: 'Custom Specs' },
  ];

  const values = [
    { key: 'production', icon: <Settings className="w-6 h-6" /> },
    { key: 'service', icon: <Shield className="w-6 h-6" /> },
    { key: 'management', icon: <Zap className="w-6 h-6" /> },
    { key: 'communication', icon: <MessageCircle className="w-6 h-6" /> },
  ];

  return (
    <div data-testid="home-page" className="electric-cursor-page">
      <ElectricCursor />
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05060A] via-[#05060A]/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05060A] via-transparent to-[#05060A]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20">
          <div className="max-w-2xl">
            <p className="text-sm text-blue-400 uppercase tracking-[0.3em] font-medium mb-6 animate-fade-in-up">
              Astravolt Transformer
            </p>
            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.1] text-white mb-6 animate-fade-in-up delay-200"
              style={{ opacity: 0 }}
            >
              {t('hero.title')}
            </h1>
            <p
              className="text-base md:text-lg text-slate-300 leading-relaxed mb-10 max-w-lg animate-fade-in-up delay-300"
              style={{ opacity: 0 }}
            >
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-400" style={{ opacity: 0 }}>
              <Link to="/products">
                <Button
                  data-testid="hero-cta-products"
                  className="bg-blue-600 text-white rounded-sm px-8 py-6 font-bold uppercase tracking-widest hover:bg-blue-500 transition-colors text-sm"
                >
                  {t('hero.cta')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="hero-cta-contact"
                  variant="outline"
                  className="bg-transparent border border-white/20 text-white rounded-sm px-8 py-6 font-bold uppercase tracking-widest hover:bg-white/10 transition-colors text-sm"
                >
                  {t('common.get_in_touch')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <button
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-white transition-colors animate-bounce"
          aria-label="Scroll down"
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </section>

      {/* About Summary Section */}
      <section className="py-20 md:py-32 bg-[#05060A] grid-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
            <Reveal className="md:col-span-5">
              <div className="relative overflow-hidden rounded-sm">
                <img src={ABOUT_IMG} alt="Astravolt Production" className="w-full h-[400px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05060A]/60 to-transparent" />
              </div>
            </Reveal>
            <Reveal className="md:col-span-7">
              <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-4">
                {t('about_section.label')}
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6 leading-tight">
                {t('about_section.title')}
              </h2>
              <p className="text-base text-slate-300 leading-relaxed mb-8">
                {t('about_section.description')}
              </p>
              <Link to="/about">
                <Button
                  data-testid="about-cta"
                  className="bg-transparent border border-white/20 text-white rounded-sm px-8 py-4 font-bold uppercase tracking-widest hover:bg-white/10 transition-colors text-sm"
                >
                  {t('about_section.read_more')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section data-testid="products-preview" className="py-20 md:py-32 bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-16">
            <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-4">
              {t('products_section.label')}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t('products_section.title')}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <Reveal key={p.key}>
                <Link to="/products" data-testid={`product-card-${p.key}`}>
                  <div
                    className="group border border-white/10 bg-[#0B0E14] overflow-hidden hover:bg-[#111620] transition-all duration-300 hover:-translate-y-1"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={p.img}
                        alt={t(`products_section.${p.key}.title`)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] to-transparent" />
                      <span className="absolute bottom-4 left-4 text-xs text-blue-400 font-medium tracking-wider">
                        {p.kva}
                      </span>
                    </div>
                    <div className="p-6 md:p-8">
                      <h3 className="text-xl font-bold text-white mb-3 tracking-tight">
                        {t(`products_section.${p.key}.title`)}
                      </h3>
                      <p className="text-sm text-slate-400 leading-relaxed line-clamp-3">
                        {t(`products_section.${p.key}.desc`)}
                      </p>
                      <span className="inline-flex items-center gap-2 text-blue-400 text-sm font-medium mt-4 group-hover:gap-3 transition-all">
                        {t('common.learn_more')}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-20 md:py-32 bg-[#05060A] relative blue-glow">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <Reveal className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t('about_section.subtitle')}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.key}>
                <div
                  className="border border-white/10 bg-[#0B0E14] p-8 hover:bg-[#111620] transition-all duration-300 hover:-translate-y-1 group"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="w-12 h-12 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400 mb-6 group-hover:bg-blue-600/20 transition-colors">
                    {v.icon}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">{t(`values.${v.key}.title`)}</h3>
                  <p className="text-xs text-blue-400 uppercase tracking-widest mb-4">{t(`values.${v.key}.subtitle`)}</p>
                  <p className="text-sm text-slate-400 leading-relaxed">{t(`values.${v.key}.desc`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability */}
      <section className="py-20 md:py-32 bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-16">
            <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-4">Astravolt</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t('sustainability.title')}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {['production_power', 'quality_val', 'customer'].map((key, i) => (
              <Reveal key={key}>
                <div className="border border-white/10 bg-[#05060A] p-8 md:p-10 hover:border-blue-600/30 transition-all duration-300">
                  <div className="text-4xl font-bold text-blue-600/30 mb-4">0{i + 1}</div>
                  <h3 className="text-lg font-bold text-white mb-3">{t(`sustainability.${key}.title`)}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{t(`sustainability.${key}.desc`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Marquee */}
      <section data-testid="certifications" className="py-16 bg-[#05060A] border-y border-white/5 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, setIndex) => (
            <div key={setIndex} className="flex items-center gap-20 px-10">
              {['ISO 9001', 'ISO 14001', 'ISO 45001', 'IEC 60076', 'KEMA', 'TSE', 'CE'].map((cert) => (
                <span key={`${setIndex}-${cert}`} className="text-2xl font-bold text-white/10 tracking-widest uppercase">
                  {cert}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6">
              {t('contact_section.subtitle')}
            </h2>
            <Link to="/contact">
              <Button
                data-testid="home-contact-cta"
                className="bg-blue-600 text-white rounded-sm px-10 py-6 font-bold uppercase tracking-widest hover:bg-blue-500 transition-colors text-sm"
              >
                {t('common.get_in_touch')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
